import React from "react";
import { Select, SelectProps } from "@mantine/core";
import { AppIcon } from "../../generic/AppIcon";

const EVENT_STATUS_OPTIONS = [
  {
    value: "100",
    label: "Enabled",
    icon: "Check",
    color: "green",
    description: "Active and visible",
  },
  {
    value: "50",
    label: "Canceled",
    icon: "X",
    color: "orange",
    description: "Caneled and visible",
  },
  {
    value: "0",
    label: "Disabled",
    icon: "X",
    color: "red",
    description: "Inactive and hidden",
  },
] as const;

export type EventStatusValue = 100 | 0;

export interface EventStatusSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: number | null;

  /** Callback when status changes */
  onChange: (value: number | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const EventStatusSelector: React.FC<EventStatusSelectorProps> = ({
  value,
  onChange,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select event status",
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  // Prepare select data with optional descriptions
  const selectData = EVENT_STATUS_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      const numValue = Number(stringValue) as EventStatusValue;
      onChange(numValue);
    }
  };

  // Get current option for icon display
  const currentOption = EVENT_STATUS_OPTIONS.find(
    (opt) => opt.value === stringValue
  );
  const leftSection =
    withIcons && currentOption ? (
      <AppIcon
        iconName={currentOption.icon as any}
        size={16}
        style={{
          color:
            currentOption.color === "green"
              ? "var(--mantine-color-green-6)"
              : "var(--mantine-color-red-6)",
        }}
      />
    ) : undefined;

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={handleChange}
      placeholder={placeholder}
      leftSection={leftSection}
      clearable={false}
    />
  );
};

// Export status options for use in other components
export { EVENT_STATUS_OPTIONS };
